import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import moment from 'moment';
import cx from 'classnames';
import { isEmpty, sortBy, last, get } from 'lodash';
import { BarLoader } from '@unite-us/ui';
import { findInsuranceByDateRange, requestDateOutOfRange } from 'src/common/utils/Dates/utils';
import useZcodes from 'src/common/hooks/useZcodes';
import IconButton from 'src/common/display/Profile/components/IconButton';
import {
  useFind,
  usePopulate,
  useFindRecord,
} from 'src/api/APIHooks';
import buildProcedureCodeInitialFormValues from 'src/components/Cases/utils/buildProcedureCodeInitialFormValues';
import './CardData.scss';
import InvoiceStatus from '../InvoiceStatus';

const CardHeader = ({
  className,
  isBillable,
  isNetworkLead,
  metafields,
  onEdit,
  onSubmit,
  providedService,
  programName,
  showInteractions,
  ignoreSocialCareExpiredDates,
  canInvoiceAboveRemainingAuthorizedAmount,
  feeScheduleProgram,
  serviceAuthorization,
  serviceAuthorizationInvoiceInformation,
  groupId,
  personId,
  hasInvoicesAccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const isEditingDisabled = providedService.state !== 'active';

  const metadata = providedService.metadata || [];

  const requiredMetaDataMissing = metafields.some(({ field, validations }) => {
    if (validations && validations.required) {
      const metaDataMatch = metadata.find((md) => md.field === field);

      if (!metaDataMatch) return true;

      const value = metaDataMatch.value || {};

      const metaDataMatchEmpty = field === 'address_field' ? (
        !value.line_1 || !value.city || !value.state || !value.postal_code
      ) : isEmpty(metaDataMatch.value);

      return metaDataMatchEmpty;
    }

    return false;
  });

  const { data: invoiceApiData, isInvoiceLoading } = useFind(
    'invoice',
    {
      provided_service: providedService.id,
      state: 'active',
    },
    {
      queryConfig: {
        placeholderData: undefined,
      },
    },
  );

  const { data: chosenPlan } = useFindRecord(
    'plan',
    providedService?.plan?.id,
    { queryConfig: { enabled: !!providedService?.plan?.id, placeholderData: undefined } },
  );
  const casePayerId = chosenPlan?.data?.data?.payer?.id;

  const { data: insurancesData } = useFind('insurance', {
    plan: providedService?.plan?.id,
    person: personId,
    state: 'active,pending,inactive',
    groupId,
  }, { queryConfig: { placeholderData: undefined, enabled: !!providedService } });

  const insurances = insurancesData?.data?.data;

  const isZcodeAssociationForced = feeScheduleProgram?.force_zcode_associations;
  const { data: zcodes } = useZcodes(feeScheduleProgram?.id, casePayerId, isZcodeAssociationForced);
  const zcodeOptions = isZcodeAssociationForced ? [] :
    zcodes;
  const shouldShowZcodeSelect = !isZcodeAssociationForced && !isEmpty(zcodeOptions);
  const zCodesNonValid = shouldShowZcodeSelect && isEmpty(providedService?.zcodes);

  const invoicesData = get(invoiceApiData, 'data.data', []);
  const invoices = sortBy(invoicesData, 'created_at');
  const lastInvoice = last(invoices);

  const insuranceRequired = feeScheduleProgram?.insurance_required;
  const startDate = moment(providedService?.starts_at);
  const endDate = providedService?.ends_at ? moment(providedService?.ends_at) : startDate;
  let validInsurance;
  if (startDate) {
    validInsurance = findInsuranceByDateRange({
      insurances,
      startDate,
      endDate: endDate || startDate,
      ignoreSocialCareExpiredDates,
      serviceAuthorization,
    });
  }

  const requestedAmountNonValid = !canInvoiceAboveRemainingAuthorizedAmount &&
    (serviceAuthorizationInvoiceInformation?.remainingAuthorizedAmount < 0 ||
      providedService?.unit_amount > serviceAuthorizationInvoiceInformation?.remainingAuthorizedAmount);

  const serviceAuthorizationStartDate = moment(serviceAuthorization?.approved_starts_at);
  const serviceAuthorizationEndDate =
    serviceAuthorization?.approved_ends_at ?
      moment(serviceAuthorization?.approved_ends_at) : serviceAuthorizationStartDate;

  const requestedDatesNonValid = serviceAuthorization && (
    requestDateOutOfRange(startDate, serviceAuthorizationStartDate, serviceAuthorizationEndDate) ||
    requestDateOutOfRange(endDate, serviceAuthorizationStartDate, serviceAuthorizationEndDate)
  );

  const placeOfServiceOptions = feeScheduleProgram?.place_of_services;
  const shouldShowPlaceOfServiceSelect = !isEmpty(placeOfServiceOptions);
  const placeOfServiceNonValid = shouldShowPlaceOfServiceSelect && !providedService?.place_of_service;

  usePopulate(
    'procedure_code_modifiers',
    'procedure_code_modifiers',
    providedService,
    { queryConfig: { placeholderData: undefined, enabled: !!providedService } },
  );

  const procedureCodes = buildProcedureCodeInitialFormValues({ providedService, feeScheduleProgram });

  const nonValidProcedureCodes = procedureCodes.some(
    (code) => !code.procedure_code || isEmpty(code.procedure_code_modifiers),
  );

  const isSubmitDisabled = requiredMetaDataMissing ||
    !providedService?.unit_amount ||
    !providedService?.starts_at ||
    (insuranceRequired && !validInsurance) ||
    requestedAmountNonValid ||
    zCodesNonValid ||
    placeOfServiceNonValid ||
    requestedDatesNonValid ||
    nonValidProcedureCodes;

  const showSubmitButton = showInteractions && isBillable && providedService.state === 'active';

  const handleSubmit = async () => {
    setSubmitting(true);
    await onSubmit();
    setSubmitting(false);
  };

  return (
    <div className={className}>
      <div className="flex justify-between space-x-4">
        <h3 className="flex-grow text-lg text-text-blue leading-snug">
          {programName} Provided
        </h3>
        {showInteractions && (
          <div className="flex flex-shrink items-start space-x-6 mt-1">
            <IconButton
              className={cx(!showSubmitButton && 'hidden')}
              disabled={isSubmitDisabled || submitting}
              icon="IconSubmit"
              id="submit-provided-service"
              labelText="Submit"
              onClick={() => handleSubmit()}
            />
            <IconButton
              disabled={isEditingDisabled}
              icon="IconPencil"
              labelText="Edit"
              id="edit-provided-service"
              onClick={onEdit}
            />
          </div>
        )}
      </div>
      {isBillable && (
        <div className="mt-2 flex space-x-2">
          <span className="text-dark-grey whitespace-nowrap">
            {moment(providedService.updated_at).utc().format('LL')}
          </span>
          {!isEmpty(invoices) && !isNetworkLead && (
            hasInvoicesAccess ? (
              <Link
                className="whitespace-nowrap pl-8"
                to={`/invoices/${lastInvoice.id}`}
              >
                {`Invoice #${lastInvoice.short_id}`}
              </Link>
            ) : (
              <span className="whitespace-nowrap pl-8">
                {`Invoice #${lastInvoice.short_id}`}
              </span>
            )
          )}
          {isInvoiceLoading ? (
            <BarLoader />
          ) : (
            <InvoiceStatus invoice={lastInvoice} />
          )}
        </div>
      )}
    </div>
  );
};

CardHeader.propTypes = {
  className: PropTypes.string,
  isBillable: PropTypes.bool.isRequired,
  isNetworkLead: PropTypes.bool,
  metafields: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  programName: PropTypes.string.isRequired,
  providedService: PropTypes.object.isRequired,
  showInteractions: PropTypes.bool.isRequired,
  ignoreSocialCareExpiredDates: PropTypes.bool.isRequired,
  canInvoiceAboveRemainingAuthorizedAmount: PropTypes.bool.isRequired,
  feeScheduleProgram: PropTypes.object.isRequired,
  serviceAuthorization: PropTypes.object.isRequired,
  serviceAuthorizationInvoiceInformation: PropTypes.object.isRequired,
  groupId: PropTypes.string.isRequired,
  personId: PropTypes.string.isRequired,
  hasInvoicesAccess: PropTypes.bool.isRequired,
};

CardHeader.defaultProps = {
  className: '',
  isNetworkLead: false,
};

export default CardHeader;
